import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Set } from '~commonInfra/utils/Set';

import { Formatters, Messages } from '../../codegen/Localize';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IDataSourceInfo } from '../../datasource/models/IDataSourceInfo';
import { AuthorizableType, getAuthorizableType } from '../../permissions/types/AuthorizableType';
import { INamedItem } from '../../spacesPlatform/models/resourceParts';
import { ConnectedPermissionsDialog, PermissionsDialogOwnProps } from '../components/PermissionsDialog';
import { GetActionableObjectsForAction } from '../requests/GetActionableObjectsForAction';
import { IGetActionableObjectsResult } from '../requests/types/GetActionableObjectsRequest';
import { PermissionsDialogStyles } from '../styles/PermissionsDialogStyle';


// Using separate strings for single items and multiple items because
// CJK languages don't have plural form so the displayed message could look werid (defect 1158040)
// Using general assets message because of Data Cloud Objects weirdness (W-13824219)
const GeneralMessagesForEditPermissionsAction = {
  permissionsDialogTitle_single: Messages.permissionsForAsset_single,
  permissionsDialogTitle_multiple: Messages.permissionsForAsset_multiple,
  cannotShowPermissions: Messages.permissions_cannotViewSelectedAssets,
};

export const ReactPermissionsDialogTestId = 'permissions-dialog-react';

export namespace EditPermissionsAction {

  export function showEditPermissionsDialog(items: Set<any>, contentType: string): Promise<any> {
    // AuthorizableType is just an enum used across permissions dialog code;
    // contentType and authorizableType are essentially the same concept.
    const authorizableType = getAuthorizableType(contentType);

    if (authorizableType === AuthorizableType.DATASOURCE && areAnyEmbeddedDatasourcesSelected(items)) {
      return Promise.resolve(alertCannotShowPermissions(
        Messages.permissions_notShowingEmbeddedAndPublishedDataSources({
          ITEM_COUNT_LOC: Formatters.formatCount(items.size()),
        })
      ));
    }

    return GetActionableObjectsForAction.editPermissions({
      type: authorizableType,
      ids: items.keys(),
    }).then((result: IGetActionableObjectsResult) => {
      const ids = result.ids;
      const unactionableItems = items.clone();
      let actionableItems = new Set<INamedItem>((item: INamedItem) => item.id);
      _.each(ids, (id: string) => {
        const item = items.get(id);
        actionableItems.add(item);
        unactionableItems.remove(item);
      });

      // if there are any items in the section that the user isn't allowed to set permissions on,
      // we don't show the permissions panel
      if (unactionableItems.size() > 0) {
        return alertCannotShowPermissions(
          getCannotShowPermissionsMessage(items)
        ).result;
      }

      const permissionsDialogProps: PermissionsDialogOwnProps = {
        authorizables: actionableItems.clone(),
        authorizableType,
      };

      return openPermissionsDialog(permissionsDialogProps);
    });
  }

  export function openPermissionsDialog(permissionsDialogProps: PermissionsDialogOwnProps) {
    const dialogTitle = getPermissionsDialogTitle(permissionsDialogProps.authorizables);

    return ReactModalService.open({
      testId: ReactPermissionsDialogTestId,
      type: DialogType.NormalWithTitleBar,
      dialogWidth: PermissionsDialogStyles.dialogContainerWidth,
      dialogStyle: { maxWidth: '90vw', maxHeight: '90vh' },
      dialogContent: React.createElement(ConnectedPermissionsDialog, permissionsDialogProps),
      disableAutoFocus: dialogElem => dialogElem.querySelector('[role="tablist"] [tabindex="0"], [role="grid"]'),
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldReturnFocusOnClose: true,
      title: dialogTitle,
    });
  }

  export function getPermissionsDialogTitle(authorizables: Set<any>): string {
    const messages = GeneralMessagesForEditPermissionsAction;

    if (messages && authorizables) {
      const itemCount = authorizables.size();
      return itemCount === 1
       ? messages.permissionsDialogTitle_single({ ITEM_NAME: authorizables.first().name })
       : messages.permissionsDialogTitle_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: Formatters.formatCount(itemCount) });
    }

    return Messages.permissions();
  }

  function alertCannotShowPermissions(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotViewPermissions(),
      message,
      dialogWidth: TabDialogWidth.width500px
    });
  }

  function getCannotShowPermissionsMessage(unactionableItems: Set<INamedItem>): string {
    const messages = GeneralMessagesForEditPermissionsAction;

    if (messages) {
      const itemCount = unactionableItems.size();
      return messages.cannotShowPermissions({
        ITEM_NAME: L10n.elideIfLong(unactionableItems.first().name),
        ITEM_COUNT: itemCount,
        ITEM_COUNT_LOC: Formatters.formatCount(itemCount),
      });
    }

    return Messages.permissions_cannotViewPermissions();
  }

  function areAnyEmbeddedDatasourcesSelected(datasources: Set<INamedItem>): boolean {
    return !!datasources.find((datasource: IDataSourceInfo) => !!datasource.workbook);
  }

}

export type EditPermissionsAction = typeof EditPermissionsAction;
